import React, { useState, useEffect } from 'react';
import { CircularProgress, Box } from '@mui/material';
import AppService from '../services/AppService';
import { getToken as getAuthToken } from '../services/auth';
import { MdPerson } from 'react-icons/md';

// Normalize any absolute URL that points to an app* host to its api* host.
// Examples:
//  https://appd.thaklis.com -> https://apid.thaklis.com
//  https://app.thaklis.com  -> https://api.thaklis.com
const normalizeApiHost = (rawUrl) => {
  try {
    if (typeof rawUrl !== 'string' || rawUrl.length === 0) return rawUrl;
    const u = new URL(rawUrl);
    const parts = u.hostname.split('.');
    if (parts.length > 0 && /^app/i.test(parts[0])) {
      parts[0] = parts[0].replace(/^app/i, 'api');
      u.hostname = parts.join('.');
      return u.toString();
    }
    return rawUrl;
  } catch (_) {
    return rawUrl; // non-absolute URL or parse error; ignore
  }
};

/**
 * Resize an image to fit within maxSize dimensions while maintaining aspect ratio
 * @param {string} imageUrl - The source image URL
 * @param {number} maxSize - Maximum width or height in pixels
 * @returns {Promise<string>} - Promise that resolves to a data URL of the resized image
 */
const resizeImage = (imageUrl, maxSize) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;
        
        // Use better image scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to data URL with good quality
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image for resizing'));
    img.src = imageUrl;
  });
};

/**
 * Component to load and display images that require authentication
 * Handles fetching the image with the auth token and converting to data URL
 * Includes retry logic for better reliability and optional image resizing
 */
const AuthenticatedImage = ({ 
  src, 
  alt = 'Authenticated Image', 
  employeeId, 
  maxSize, 
  onLoad, 
  onError, 
  style, 
  ...props 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const MAX_RETRIES = 2;

  useEffect(() => {
    let isMounted = true;
    let controller = new AbortController();

    if (!src && !employeeId) {
      console.error('AuthenticatedImage: No src or employeeId provided');
      setLoading(false);
      setError(true);
      return;
    }

    const fetchImage = async (retryAttempt = 0) => {
      if (!isMounted) return;
      
      console.log('=== AUTHENTICATED IMAGE DEBUG ===');
      console.log('Original src prop:', src);
      console.log('Employee ID prop:', employeeId);
      console.log('Max size prop:', maxSize);
      console.log('Retry attempt:', retryAttempt);
      
      const token = getAuthToken();
      if (!token) {
        console.error('No authentication token found');
        setError(true);
        setLoading(false);
        if (onError) onError(new Error('No authentication token found'));
        return;
      }
      
      console.log('Token found, length:', token.length);

      // Handle various URL formats and clean them up
      let processedSrc = src;
      
      // Special case: data:image/jpeg;base64,/v1/users/... is actually an API path with incorrect prefix
      if (processedSrc && typeof processedSrc === 'string' && processedSrc.startsWith('data:image/jpeg;base64,/v1/users/')) {
        console.log('Found API path with incorrect data URL prefix, fixing:', processedSrc);
        processedSrc = processedSrc.replace('data:image/jpeg;base64,', '');
        console.log('Extracted API path:', processedSrc);
      }
      
      // Extract employee ID from URL if not provided as prop
      let employeeIdFromUrl = null;
      if (processedSrc && typeof processedSrc === 'string') {
        const employeeIdMatch = processedSrc.match(/\/users\/([^\/]+)\//);
        if (employeeIdMatch) {
          employeeIdFromUrl = employeeIdMatch[1];
        }
      }
      const effectiveEmployeeId = employeeId || employeeIdFromUrl || 'unknown';

      try {
        setLoading(true);
        setError(false);
        
        // Construct the full URL using AppService (no token in query)
        let fullUrl;
        
        // Check if we have an employee ID but no valid URL, construct a direct photo endpoint URL
        if (effectiveEmployeeId && effectiveEmployeeId !== 'unknown' && 
            (!processedSrc || processedSrc === '' || processedSrc === 'null' || processedSrc === 'undefined')) {
          // Construct a direct URL to the employee photo endpoint using the employee ID
          // Use camelCase 'photo' document type for consistency with backend changes
          const baseUrl = AppService.getBaseUrl();
          fullUrl = `${baseUrl}/v1/users/${effectiveEmployeeId}/documents/photo/download`;
          console.log(`Constructed direct photo endpoint URL for employee ${effectiveEmployeeId}:`, fullUrl);
        }
        // If the URL is a relative path (starts with /), prepend the base URL
        else if (processedSrc && processedSrc.startsWith('/')) {
          // Use the API base URL from AppService
          const baseUrl = AppService.getBaseUrl();
          fullUrl = `${baseUrl}${processedSrc}`;
          console.log(`Constructed full URL for employee ${effectiveEmployeeId}:`, fullUrl);
        } 
        // If it's not an absolute URL and not a relative URL starting with /
        else if (processedSrc && !processedSrc.startsWith('http')) {
          // assume it's a relative path without the leading slash
          const baseUrl = AppService.getBaseUrl();
          fullUrl = `${baseUrl}/${processedSrc}`;
          console.log(`Constructed full URL for relative path for employee ${effectiveEmployeeId}:`, fullUrl);
        } 
        // Use the provided URL directly if it's absolute
        else if (processedSrc) {
          // If absolute, normalize host app* -> api*
          fullUrl = normalizeApiHost(`${processedSrc}`);
          console.log(`Using provided URL directly for employee ${effectiveEmployeeId}:`, fullUrl);
        }
        // Fallback if we somehow don't have a URL
        else {
          console.error(`No valid URL could be constructed for employee ${effectiveEmployeeId}`);
          setError(true);
          setLoading(false);
          if (onError) onError(new Error('No valid URL could be constructed'));
          return;
        }
        
        // Ensure absolute URLs are normalized before fetching
        if (fullUrl && /^https?:\/\//i.test(fullUrl)) {
          fullUrl = normalizeApiHost(fullUrl);
        }

        // Add a timeout to the fetch request
        const timeoutId = setTimeout(() => {
          controller.abort();
          console.error(`Fetch timeout for employee ${effectiveEmployeeId}`);
          
          // Retry on timeout if we haven't exceeded max retries
          if (retryAttempt < MAX_RETRIES && isMounted) {
            console.log(`Retrying fetch for employee ${effectiveEmployeeId} (attempt ${retryAttempt + 1})`);
            // Create a new controller for the retry
            controller = new AbortController();
            fetchImage(retryAttempt + 1);
          } else if (isMounted) {
            setError(true);
            setLoading(false);
            if (onError) onError(new Error(`Fetch timeout after ${retryAttempt} attempts`));
          }
        }, 10000); // 10 second timeout
        
        // Make the authenticated fetch request with Authorization header
        console.log(`Fetching image for employee ${effectiveEmployeeId} from URL:`, fullUrl);
        
        const response = await fetch(fullUrl, {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Clear the timeout since the request completed
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Failed to load image for employee ${effectiveEmployeeId}: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        // Check if the blob is empty or invalid
        if (!blob || blob.size === 0) {
          console.error(`Empty blob received for employee ${effectiveEmployeeId}`);
          throw new Error('Empty image data received');
        }
        
        // Create an object URL from the blob
        const objectUrl = URL.createObjectURL(blob);
        
        // If maxSize is specified, resize the image
        if (maxSize && isMounted) {
          try {
            const resizedUrl = await resizeImage(objectUrl, maxSize);
            setImageUrl(resizedUrl);
            // Clean up the original object URL since we have a resized version
            URL.revokeObjectURL(objectUrl);
          } catch (resizeError) {
            console.warn(`Failed to resize image for employee ${effectiveEmployeeId}, using original:`, resizeError);
            // Fall back to original image if resize fails
            setImageUrl(objectUrl);
          }
        } else if (isMounted) {
          setImageUrl(objectUrl);
        }
        
        if (isMounted) {
          setLoading(false);
          if (onLoad) onLoad();
        }
      } catch (fetchError) {
        // Retry on network errors if we haven't exceeded max retries
        if (retryAttempt < MAX_RETRIES && isMounted) {
          console.log(`Retrying after fetch error for employee ${effectiveEmployeeId} (attempt ${retryAttempt + 1}):`, fetchError.message);
          // Create a new controller for the retry
          controller = new AbortController();
          // Add a small delay before retrying
          setTimeout(() => {
            if (isMounted) fetchImage(retryAttempt + 1);
          }, 1000 * (retryAttempt + 1)); // Exponential backoff
        } else if (isMounted) {
          console.error(`All retry attempts failed for employee ${effectiveEmployeeId}:`, fetchError);
          setError(true);
          setLoading(false);
          if (onError) onError(fetchError);
        }
      }
    };

    // Start the fetch process
    fetchImage(0);

    // Cleanup function
    return () => {
      isMounted = false;
      controller.abort();
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [src, employeeId, maxSize, onLoad, onError]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        style={style}
        {...props}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error || !imageUrl) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        style={style}
        {...props}
      >
        <MdPerson size={24} />
      </Box>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      style={style}
      {...props}
    />
  );
};

export default AuthenticatedImage;
